import { Plus, Trash2 } from "lucide-mui";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import toast from "react-hot-toast";
import type { InferenceRental, InferenceRentalOffer } from "../../../api/client";
import { api } from "../../../api/client";
import { keys } from "../../../api/queryKeys";
import CenteredSpinner from "../../../components/CenteredSpinner.tsx";
import ConfirmDialog from "../../../components/ConfirmDialog.tsx";
import FormattedDate from "../../../components/FormattedDate.tsx";
import Panel from "../../../components/Panel.tsx";
import RowActions from "../../../components/RowActions.tsx";
import SecretCredentialForm from "../../../components/SecretCredentialForm.tsx";
import StatusChip from "../../../components/StatusChip.tsx";
import EmptyTableRow from "../../../components/table/EmptyTableRow.tsx";
import ScrollTable from "../../../components/table/ScrollTable.tsx";
import { formatMoney, formatNumber } from "../../../utils/format.ts";

function perHour(value: number | null | undefined): string {
  return value == null ? "—" : `${formatMoney(value, "USD", { decimals: 3 })}/h`;
}

function RentDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [target, setTarget] = useState<InferenceRentalOffer | null>(null);
  const offersQ = useQuery({
    queryKey: keys.inferenceRental.offers(),
    queryFn: () => api.listInferenceRentalOffers(),
    // Offers come and go by the minute: ask again each time the dialog opens.
    staleTime: 0,
    gcTime: 0,
  });
  const rent = useMutation({
    mutationFn: (offer: InferenceRentalOffer) => api.createInferenceRental(offer.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.inferenceRental.all });
      toast.success("Renting the box, its servers join the pool once they register");
      onClose();
    },
  });
  const offers = offersQ.data ?? [];

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Rent a GPU box</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          2x RTX 3090 on VAST.ai in Europe, serving Clef-flash (one server per GPU). Billed by the hour from
          the moment it is rented until it is destroyed. It takes about 15 minutes to boot.
        </Typography>
        {offersQ.isLoading ? (
          <CenteredSpinner />
        ) : (
          <ScrollTable>
            <TableHead>
              <TableRow>
                <TableCell>Location</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Reliability</TableCell>
                <TableCell>Download</TableCell>
                <TableCell>CUDA</TableCell>
                <TableCell padding="checkbox" />
              </TableRow>
            </TableHead>
            <TableBody>
              {offers.map((offer) => (
                <TableRow key={offer.id} hover>
                  <TableCell>
                    <Typography variant="body2">{offer.geolocation ?? "—"}</Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      Machine {offer.machine_id ?? "?"}
                    </Typography>
                  </TableCell>
                  <TableCell>{perHour(offer.dph_total)}</TableCell>
                  <TableCell>
                    {offer.reliability2 == null ? "—" : `${(offer.reliability2 * 100).toFixed(1)}%`}
                  </TableCell>
                  <TableCell>
                    {offer.inet_down == null ? "—" : `${formatNumber(Math.round(offer.inet_down))} Mbps`}
                  </TableCell>
                  <TableCell>{offer.cuda_max_good ?? "—"}</TableCell>
                  <TableCell padding="checkbox" align="right">
                    <Button size="small" variant="outlined" onClick={() => setTarget(offer)}>
                      Rent
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {offers.length === 0 && (
                <EmptyTableRow colSpan={6}>No 2x RTX 3090 offers match right now</EmptyTableRow>
              )}
            </TableBody>
          </ScrollTable>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
      {target && (
        <ConfirmDialog
          open
          title={`Rent a box: ${perHour(target.dph_total)}`}
          confirmLabel="Rent box"
          color="warning"
          pending={rent.isPending}
          onConfirm={() => rent.mutate(target)}
          onClose={() => setTarget(null)}
        >
          {target.geolocation ?? "A host"} bills {perHour(target.dph_total)} until you destroy the
          box here.
        </ConfirmDialog>
      )}
    </Dialog>
  );
}

// No Destroy for these: torn down already or on its way.
const ENDED = new Set(["destroying", "destroyed", "failed"]);
// Still held (and billed) at VAST.
const BILLED = new Set(["creating", "booting", "ready", "destroying"]);

function rentalLine(rental: InferenceRental): string {
  if (rental.status === "failed") return rental.error ?? "Failed";
  if (rental.status === "ready") return "Both GPUs serving";
  return rental.status_message ?? "";
}

/** Rented GPU boxes (VAST.ai): their API key, the boxes, rent and destroy. */
export default function InferenceRentalsPanel() {
  const queryClient = useQueryClient();
  const [secret, setSecret] = useState("");
  const [renting, setRenting] = useState(false);

  const credentialsQ = useQuery({
    queryKey: keys.inferenceRental.credentials(),
    queryFn: api.getInferenceRentalCredentials,
  });
  const rentalsQ = useQuery({
    queryKey: keys.inferenceRental.list(),
    queryFn: api.listInferenceRentals,
    meta: { live: true },
  });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: keys.inferenceRental.all });

  const saveKey = useMutation({
    mutationFn: () => api.putInferenceRentalCredentials(secret),
    onSuccess: () => {
      setSecret("");
      invalidate();
      toast.success("VAST.ai API key saved");
    },
  });
  const clearKey = useMutation({
    mutationFn: api.deleteInferenceRentalCredentials,
    onSuccess: () => {
      invalidate();
      toast.success("VAST.ai API key removed");
    },
  });
  const destroy = useMutation({
    mutationFn: (id: string) => api.destroyInferenceRental(id),
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: keys.inferenceInstance.all });
      toast.success("Box destroyed");
    },
  });

  const credentials = credentialsQ.data;
  const rentals = rentalsQ.data ?? [];
  // Ended rentals stay listed for a day, so a failed one says why.
  const live = rentals.filter((r) => BILLED.has(r.status));
  const hourly = live.reduce((sum, r) => sum + (r.dph_total ?? 0), 0);

  return (
    <Stack spacing={2} sx={{ mt: 3 }}>
      <Panel
        title="Rented GPU boxes"
        subtitle={
          live.length
            ? `Decision-model capacity rented from VAST.ai · ${perHour(hourly)} in total`
            : "Decision-model capacity rented from VAST.ai by the hour"
        }
        count={live.length}
        disablePadding
        action={
          <Button
            variant="outlined"
            size="small"
            startIcon={<Plus fontSize="small" />}
            disabled={!credentials?.configured}
            onClick={() => setRenting(true)}
          >
            Rent a box
          </Button>
        }
      >
        <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell>Box</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Rented</TableCell>
              <TableCell padding="checkbox" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rentals.map((rental) => (
              <TableRow key={rental.id} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {rental.offer.gpu_name ?? "GPU box"} x{rental.offer.num_gpus ?? "?"}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {rental.offer.geolocation ?? "—"}
                    {rental.external_id != null && ` · VAST ${rental.external_id}`}
                  </Typography>
                </TableCell>
                <TableCell>
                  <StatusChip kind="inference_rental" value={rental.status} />
                  <Typography
                    variant="caption"
                    sx={{ display: "block", color: "text.secondary", maxWidth: 320 }}
                    noWrap
                    title={rentalLine(rental)}
                  >
                    {rentalLine(rental)}
                  </Typography>
                </TableCell>
                <TableCell>{perHour(rental.dph_total)}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                    {rental.public_ip ?? "—"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    <FormattedDate value={rental.created_at} />
                  </Typography>
                </TableCell>
                <TableCell padding="checkbox" align="right">
                  <RowActions
                    label={`VAST ${rental.external_id ?? rental.id.slice(0, 8)}`}
                    actions={[
                      {
                        label: "Destroy",
                        icon: <Trash2 fontSize="small" />,
                        destructive: true,
                        hidden: ENDED.has(rental.status),
                        onClick: () => destroy.mutateAsync(rental.id),
                        confirm: {
                          title: "Destroy box",
                          body: "Its servers leave the pool now and billing stops.",
                        },
                      },
                    ]}
                  />
                </TableCell>
              </TableRow>
            ))}
            {rentals.length === 0 && (
              <EmptyTableRow colSpan={6}>
                {credentials?.configured
                  ? "No boxes rented"
                  : "Add a VAST.ai API key below to rent boxes"}
              </EmptyTableRow>
            )}
          </TableBody>
        </ScrollTable>
      </Panel>

      {credentials && (
        <Panel title="VAST.ai API key">
          <Box sx={{ maxWidth: 640 }}>
            <SecretCredentialForm
              name="VAST.ai API key"
              description="Rents and destroys the boxes above. Give it instance read and write only."
              stored={credentials.configured}
              hint={credentials.secret_hint}
              updatedAt={credentials.updated_at}
              encryptionConfigured={credentials.encryption_configured}
              encryptionWarning="the key can't be stored."
              canManage
              value={secret}
              onChange={setSecret}
              fieldLabel={credentials.configured ? "New API key" : "API key"}
              helperText="Write-only — the stored value is never shown again."
              onSave={() => saveKey.mutate()}
              saving={saveKey.isPending}
              saveDisabled={!secret.trim()}
              onClear={() => clearKey.mutateAsync()}
              clearConfirm={{ title: "Remove the VAST.ai API key", confirmLabel: "Remove" }}
            />
          </Box>
        </Panel>
      )}

      {renting && <RentDialog onClose={() => setRenting(false)} />}
    </Stack>
  );
}
